// « Relecture Vega » — the seed's history, and the operator's manual run
// (`docs/VEGA-SEED-BRIEF.md` §12.1, Q3: « nightly + manual »).
//
// Every half hour the PMS reads the Vega mirror into its own tables, once per
// import. This tab shows what each pass did and — the point of it — which
// bookings it REFUSED or REPORTED, by number: until now that lived in
// `pms_seed_run.report`, readable only with SQL (the 2026-10-21 incident).
//
// The button asks for a pass NOW. It is a request, not the run: a pass takes
// 7–9 s and a signed-in call is cut at 8 s, so a job runs it within the minute
// and the screen follows it. Nothing here writes to Vega — it reads Vega into
// the PMS, never the other way round. That is why it lives beside « Miroir
// Vega » but is not part of that read-only port.
//
// Operator-only: `pms.seed.manage`, gated again in both RPCs; hiding the tab is
// only the courtesy of not offering what would be refused (ARCHITECTURE §1).
//
// ⚠ « Relecture », not « synchronisation »: the GLOSSARY gives « Synchronisation »
// to the Channex push.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { SeedAttention, SeedOverview, SeedRun } from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { usePms, usePmsScope } from './PmsProvider.js';

/** While a request is pending the screen follows it; otherwise it only reloads when asked. */
const FOLLOW_MS = 4000;

/** The seed's own words for what it refused or reported, in French. A kind this
 *  build does not know is shown under its raw name — never dropped. */
export const ATTENTION_FR: Record<string, string> = {
  chargesRefusees: 'Charges refusées',
  planNonReparti: 'Plan non réparti par chambre',
  planVegaAbsente: 'Plan Vega absent',
  codeNonCartographie: 'Code Vega non cartographié',
  categorieApprochee: 'Catégorie approchée',
  sourceInconnue: 'Source inconnue',
  groupeSansNomUnique: 'Groupe sans nom unique',
  refus: 'Réservation refusée',
  annulationRefusee: 'Annulation refusée',
  netNegatif: 'Net négatif',
  chambreSansLogement: 'Chambre sans logement',
  avoirPuisRefacturation: 'Avoir puis refacturation',
};

const TRIGGER_FR = { cron: 'Automatique', manual: 'Manuelle', backfill: 'Historique' } as const;

const WHEN_FMT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Paris',
});
const HOUR_FMT = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' });

/** « 21/10 14:00 » — the Paris wall clock, which is the desk's. */
export function whenFr(iso: string): string {
  return WHEN_FMT.format(new Date(iso)).replace(',', '');
}
const hourFr = (iso: string) => HOUR_FMT.format(new Date(iso));

const n = (v: number | null) => (v === null ? '—' : String(v));

/** What the last pass of any kind says — a no-op included: it is what says « le seed tourne ». */
export function checkLine(o: SeedOverview): string {
  const c = o.lastCheck;
  if (!c) return 'Aucun passage enregistré pour cet établissement.';
  const at = whenFr(c.startedAt);
  const imp = c.importId === null ? '' : ` — import ${c.importId}`;
  if (c.status === 'noop') return `Dernier passage : ${at}${imp} — rien à faire (${c.reason ?? 'import déjà relu'}).`;
  if (c.status === 'failed') return `Dernier passage : ${at}${imp} — échec.`;
  return `Dernier passage : ${at}${imp} — ${TRIGGER_FR[c.trigger].toLowerCase()}, appliqué.`;
}

export interface SeedRunsState {
  allowed: boolean;
  loading: boolean;
  error: string | null;
  overview: SeedOverview | null;
}

interface ViewProps {
  state: SeedRunsState;
  /** Asks for a relecture. Resolves to a message when it was refused, null when recorded. */
  onRequest: () => Promise<string | null>;
  onRefresh: () => void;
  /** Overridable so a test need not touch `window`. */
  confirmFn?: (message: string) => boolean;
}

export const CONFIRM_TEXT =
  'Relancer la relecture de Vega maintenant ?\n\n' +
  'Le PMS relit l’import Vega le plus récent et met à jour ses réservations, ses charges, ' +
  'ses règlements et ses factures — comme il le fait toutes les 30 minutes. ' +
  'Rien n’est écrit dans Vega. La relecture démarre dans la minute.';

export function SeedRunsView({ state, onRequest, onRefresh, confirmFn }: ViewProps) {
  const { allowed, loading, error, overview } = state;
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const [open, setOpen] = useState<ReadonlySet<number>>(new Set());

  if (!allowed) {
    return (
      <div className="occ">
        <h1>Relecture Vega</h1>
        <p className="muted">
          Cet onglet demande le droit de gérer la relecture Vega (pms.seed.manage).
        </p>
      </div>
    );
  }

  const pending = overview?.pending ?? null;
  const ask = async () => {
    if (!(confirmFn ?? ((m: string) => window.confirm(m)))(CONFIRM_TEXT)) return;
    setAsking(true);
    setAskError(null);
    const msg = await onRequest();
    setAsking(false);
    if (msg) setAskError(msg);
  };
  const toggle = (id: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="pms-seed" data-testid="seed-runs">
      <p className="muted">
        Le PMS relit le miroir Vega toutes les 30 minutes, une fois par import. Vous pouvez
        demander une relecture maintenant : elle s’exécute dans la minute et n’écrit jamais dans
        Vega. Les réservations refusées ou signalées apparaissent ci-dessous, avec leur numéro.
      </p>

      {error && (
        <p className="occ-error">
          ⚠ {error} — ne pas en conclure qu’aucune relecture n’a eu lieu.
        </p>
      )}
      {loading && !overview && <p className="muted">Chargement…</p>}

      {overview && (
        <>
          <p className="muted" data-testid="seed-check">{checkLine(overview)}</p>

          <p>
            <button
              type="button"
              className="pms-chip"
              disabled={asking || pending !== null}
              onClick={() => void ask()}
            >
              Relancer la relecture maintenant
            </button>{' '}
            <button type="button" className="pms-link" onClick={onRefresh} disabled={loading}>
              Actualiser
            </button>
            {askError && <span className="occ-neg"> ⚠ {askError}</span>}
          </p>

          {pending && (
            <p className="occ-warn" role="status" data-testid="seed-pending">
              Demande n° {pending.id} enregistrée à {hourFr(pending.requestedAt)} — la relecture
              démarre dans la minute…
            </p>
          )}
          {!pending && overview.lastRequest?.status === 'failed' && (
            <p className="occ-error" data-testid="seed-last-request">
              ⚠ La dernière demande (n° {overview.lastRequest.id}, {whenFr(overview.lastRequest.requestedAt)}) a
              échoué : {overview.lastRequest.error ?? 'raison inconnue'}. Rien n’a été modifié.
            </p>
          )}
          {!pending && overview.lastRequest?.status === 'done' && (
            <p className="muted" data-testid="seed-last-request">
              Dernière demande : n° {overview.lastRequest.id}, exécutée{' '}
              {overview.lastRequest.finishedAt ? `à ${hourFr(overview.lastRequest.finishedAt)} ` : ''}
              {overview.lastRequest.runId !== null ? `(passage n° ${overview.lastRequest.runId})` : ''}.
            </p>
          )}

          <table className="occ-table">
            <thead>
              <tr>
                <th>Passage</th>
                <th>Début</th>
                <th>Déclencheur</th>
                <th>Import</th>
                <th title="Réservations lues · insérées · modifiées · annulées">Réservations</th>
                <th title="Charges écrites · annulées par ce passage">Charges</th>
                <th title="Réservations refusées ou signalées par ce passage">À vérifier</th>
              </tr>
            </thead>
            <tbody>
              {overview.runs.map((r) => (
                <RunRows key={r.id} run={r} open={open.has(r.id)} onToggle={() => toggle(r.id)} />
              ))}
              {overview.runs.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">
                    Aucune relecture n’a encore agi (les passages sans changement ne sont pas listés).
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function RunRows({ run, open, onToggle }: { run: SeedRun; open: boolean; onToggle: () => void }) {
  const b = run.bookings;
  const c = run.charges;
  return (
    <>
      <tr>
        <th scope="row">n° {run.id}</th>
        <td className="muted">{whenFr(run.startedAt)}</td>
        <td className="muted">{TRIGGER_FR[run.trigger]}</td>
        <td className="muted">{run.importId ?? '—'}</td>
        <td className="muted">
          {run.status === 'failed' ? (
            <span className="occ-neg">échec</span>
          ) : (
            `${n(b.seen)} · +${n(b.inserted)} · ~${n(b.updated)} · −${n(b.cancelled)}`
          )}
        </td>
        <td className="muted">
          {c === null ? '—' : c.state !== null ? <span title={c.state}>non écrites</span> : `+${n(c.inserted)} · −${n(c.voided)}`}
        </td>
        <td>
          {run.attentionTotal === 0 ? (
            <span className="occ-ok">Rien</span>
          ) : (
            <button type="button" className="pms-link occ-warn" onClick={onToggle} aria-expanded={open}>
              {run.attentionTotal} à voir {open ? '▾' : '▸'}
            </button>
          )}
        </td>
      </tr>
      {open && run.attentionTotal > 0 && (
        <tr className="pms-seed-detail">
          <td colSpan={7}>
            <ul data-testid={`seed-attention-${run.id}`}>
              {run.attention.map((a, i) => (
                <AttentionRow key={`${a.kind}-${a.resa ?? ''}-${i}`} a={a} />
              ))}
            </ul>
            {run.attentionTotal > run.attention.length && (
              <p className="muted">
                {run.attention.length} premières sur {run.attentionTotal} : le passage en porte plus.
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function AttentionRow({ a }: { a: SeedAttention }) {
  return (
    <li>
      <strong>{ATTENTION_FR[a.kind] ?? a.kind}</strong>
      {a.resa ? <> — résa {a.resa}</> : null}
      {a.detail ? <span className="muted"> : {a.detail}</span> : null}
    </li>
  );
}

/** The container: reads through the port, asks through the port, and follows a pending request. */
export function SeedRuns() {
  const { vegaSeed } = usePms();
  const { current } = usePmsScope();
  const allowed = useCapability(CAPABILITIES.PMS_SEED_MANAGE);
  const [overview, setOverview] = useState<SeedOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!current || !allowed) return;
    setLoading(true);
    const res = await vegaSeed.overview(current);
    if (!alive.current) return;
    setLoading(false);
    if (!res.ok) {
      setError(res.error.message ?? 'lecture impossible');
      return;
    }
    setError(null);
    setOverview(res.value);
  }, [vegaSeed, current, allowed]);

  useEffect(() => {
    setOverview(null);
    void load();
  }, [load]);

  // While a request waits for the job, follow it — and stop when it has run.
  const pending = overview?.pending ?? null;
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => void load(), FOLLOW_MS);
    return () => clearInterval(t);
  }, [pending, load]);

  const request = useCallback(async () => {
    if (!current) return 'aucun établissement choisi';
    const res = await vegaSeed.requestRun(current);
    if (!res.ok) return res.error.message ?? 'demande refusée';
    await load();
    return null;
  }, [vegaSeed, current, load]);

  return <SeedRunsView state={{ allowed, loading, error, overview }} onRequest={request} onRefresh={() => void load()} />;
}
